// Every programme in the institute, in one paged table (the Programmes tab
// of the Departments page). Search, a department filter and paging run here
// on the full list: listProgrammes returns all live programmes, and an
// institute has at most a few hundred.
//
// A row opens the programme's subjects; its menu also opens the
// department's programmes (create / rename / remove live there).
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, BookOpen, GraduationCap, Loader2, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RowActions } from "@/components/RowActions";
import { DataPager } from "@/components/DataPager";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useInstituteLabels } from "@/lib/instituteLabels";
import { type DepartmentProfile, type Programme, listProgrammes } from "./structureApi";
import { ProgrammeSubjectsDialog } from "./ProgrammeSubjectsDialog";

const PAGE_SIZE = 20;

type Props = {
    departments: { rawName: string; name: string; profile: DepartmentProfile }[];
    canManage: boolean;
    reloadKey: number;                               // bump to fetch again
    onOpenDepartment: (department: DepartmentProfile) => void;
};

export function ProgrammesTable({ departments, canManage, reloadKey, onOpenDepartment }: Props) {
    const L = useInstituteLabels();
    const [ programmes, setProgrammes ] = useState<Programme[]>([]);
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState<string | null>(null);
    const [ search, setSearch ] = useState("");
    const [ department, setDepartment ] = useState<string | null>(null);
    const [ page, setPage ] = useState(1);
    const [ subjectsOf, setSubjectsOf ] = useState<Programme | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setProgrammes(await listProgrammes());
        } catch (err: any) {
            setError(err?.message || `Failed to load ${L.programmes.toLowerCase()}`);
        } finally {
            setLoading(false);
        }
    }, [ L.programmes ]);

    useEffect(() => { load(); }, [ load, reloadKey ]);

    const departmentName = useCallback((key: string) =>
        departments.find(d => d.rawName === key)?.name ?? key.replace(/_/g, " "), [ departments ]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return programmes.filter(p =>
            (!department || p.departmentName === department) &&
            (!q || p.name.toLowerCase().includes(q) || departmentName(p.departmentName).toLowerCase().includes(q)));
    }, [ programmes, search, department, departmentName ]);

    // a new search or filter starts on page 1
    useEffect(() => { setPage(1); }, [ search, department ]);

    const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const current = Math.min(page, pages);
    const shown = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

    return (
        <>
            <div className="flex flex-col gap-3 border-b px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <p className="font-semibold leading-none">{L.programmes}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {filtered.length} {L.programme.toLowerCase()}(s){department || search ? ` of ${programmes.length}` : ""}
                    </p>
                </div>
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                    <div className="w-full sm:w-56">
                        <SearchableSelect
                            value={department}
                            nullLabel={`All ${L.departments.toLowerCase()}`}
                            searchPlaceholder={`Search ${L.departments.toLowerCase()}…`}
                            emptyText={`No ${L.departments.toLowerCase()} found.`}
                            options={departments.map(d => ({ value: d.rawName, label: d.name }))}
                            onChange={setDepartment}
                        />
                    </div>
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input className="h-9 pl-9" placeholder={`Search ${L.programmes.toLowerCase()}…`}
                            value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                </div>
            </div>

            {loading && programmes.length === 0 ? (
                <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : error ? (
                <div className="flex h-48 flex-col items-center justify-center gap-2">
                    <AlertCircle className="h-8 w-8 text-red-400" />
                    <p className="text-sm text-muted-foreground">{error}</p>
                    <Button variant="outline" size="sm" onClick={load}>Retry</Button>
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex h-48 flex-col items-center justify-center gap-2">
                    <GraduationCap className="h-10 w-10 text-muted-foreground/20" />
                    <p className="text-sm text-muted-foreground">
                        {programmes.length === 0
                            ? `No ${L.programmes.toLowerCase()} yet. Create them from a ${L.department.toLowerCase()}'s menu.`
                            : `No ${L.programmes.toLowerCase()} match.`}
                    </p>
                </div>
            ) : (
                <>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="pl-6">{L.programme}</TableHead>
                                <TableHead>{L.department}</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Created</TableHead>
                                <TableHead className="w-12 pr-4"><span className="sr-only">Actions</span></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {shown.map(p => {
                                const dept = departments.find(d => d.rawName === p.departmentName);
                                return (
                                    <TableRow key={p.id} className="cursor-pointer" onClick={() => setSubjectsOf(p)}>
                                        <TableCell className="pl-6">
                                            <div className="flex items-center gap-2">
                                                <div className="rounded-md bg-primary/10 p-1"><GraduationCap className="h-4 w-4 text-primary" /></div>
                                                <span className="font-medium">{p.name}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm">{departmentName(p.departmentName)}</TableCell>
                                        <TableCell>
                                            {p.singleClassroom
                                                ? <Badge variant="secondary" className="text-[11px]">Single {L.classroom.toLowerCase()}</Badge>
                                                : <span className="text-xs text-muted-foreground">{L.classrooms}</span>}
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : "—"}
                                        </TableCell>
                                        <TableCell className="w-12 pr-4 text-right">
                                            <RowActions label={p.name} actions={[
                                                { label: L.subjects, icon: BookOpen, onSelect: () => setSubjectsOf(p) },
                                                {
                                                    label: `${L.programmes} of this ${L.department.toLowerCase()}`, icon: GraduationCap,
                                                    onSelect: () => dept && onOpenDepartment(dept.profile), hidden: !dept,
                                                },
                                            ]} />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>

                    <DataPager className="border-t px-6 py-3" page={current} pageSize={PAGE_SIZE} total={filtered.length} onPage={setPage} loading={loading} />
                </>
            )}

            <ProgrammeSubjectsDialog programme={subjectsOf} canManage={canManage} onClose={() => setSubjectsOf(null)} />
        </>
    );
}
